import moment from "moment";

// Matches UK postcodes and Irish Eircodes (routing keys: A C D E F H K N P R T V W X Y).
const UK_POSTCODE_REGEX = /^(GIR\s?0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y])?)|[0-9][A-HJKPS-UW])\s?[0-9][ABD-HJLNP-UW-Z]{2}|[ACDEFHKNPRTVWXY][0-9]{2}\s?[A-Z0-9]{4})$/i;

// TD-192: fallback only. The slot-list response states its own zone in
// `timezone` (e.g. "Australia/Sydney" on the AU platform) — always prefer that
// over this default, which just preserves pre-AU behaviour when it is absent.
const DEFAULT_SLOT_TIME_ZONE = "Europe/London";
const UTC_ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;

// True once the sf_slot_utc_conversion backend flag has converted a slot into a
// full UTC instant — false for the legacy bare "HH:MM" string. Self-describing,
// so the same slot-picker code handles both response shapes.
export function isUtcIsoDateTime(value) {
  return typeof value === "string" && UTC_ISO_DATETIME_REGEX.test(value.trim());
}

// Reads a UTC instant's wall-clock date/time in a given IANA zone without
// moment-timezone (not a dependency here) — Intl.DateTimeFormat carries its own
// tz database. Guards the Node/ICU "24:00 instead of 00:00" midnight quirk.
function getWallClockParts(instant, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = {};
  formatter.formatToParts(instant).forEach(({ type, value }) => {
    parts[type] = value;
  });
  if (parts.hour === "24") parts.hour = "00";
  return parts;
}

// Formats a slot for the date strip and timeslot buttons. `timeZone` is the
// member's stored preference (TD-192) — the device clock is only the fallback
// for a host that has none, since a travelling member's machine is not where
// they want their appointments quoted. Bare "HH:MM" slots pass through as-is.
export function formatSlotForDisplay(value, timeZone) {
  if (!isUtcIsoDateTime(value)) return value;
  if (!timeZone) return moment(value).format("HH:mm");
  const { hour, minute } = getWallClockParts(new Date(value), timeZone);
  return `${hour}:${minute}`;
}

// A slot's calendar day in `timeZone`. 21:00 Monday UTC is already Tuesday in
// Sydney, so the day a slot is filed under has to follow the display zone too —
// otherwise a slot shows under a date it isn't on.
function localDateKey(instant, timeZone) {
  if (!timeZone) return moment(instant).format("YYYY-MM-DD");
  const { year, month, day } = getWallClockParts(new Date(instant), timeZone);
  return `${year}-${month}-${day}`;
}

// Names the zone slots are displayed in, for headings above a time picker — the
// member's preference when there is one, otherwise the device zone (see
// formatSlotForDisplay). Never the API's region, and never slotTimeZone, which
// is only what the booking payload is quoted in.
//
// Reported as the IANA identifier ("Europe/London") to match the mobile app
// rather than the descriptive name Intl would give ("British Summer Time"), so
// the two platforms read the same. Returns "" when no zone can be resolved, so
// callers drop the suffix instead of printing empty brackets.
export function getViewerTimeZoneLabel(timeZoneOverride) {
  try {
    const timeZone =
      timeZoneOverride || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) return "";
    // Throws on an identifier the runtime does not know, which is what keeps a
    // bad zone from being printed back at the member verbatim.
    new Intl.DateTimeFormat("en-GB", { timeZone }).format(new Date());
    return timeZone;
  } catch (error) {
    return "";
  }
}

// book-appointment's start_date/start_time stay naive wall-clock in the slot
// list's own zone — NOT the viewer's and not UTC, so posting the raw "Z" string
// would book the wrong time. A slot still in the legacy bare-HH:MM shape is
// already that wall-clock, so it passes through and the caller supplies the date.
export function formatAppointmentTimeForApi(value, timeZone) {
  if (!isUtcIsoDateTime(value)) {
    return { startDate: null, startTime: value };
  }
  const { year, month, day, hour, minute } = getWallClockParts(
    new Date(value),
    timeZone || DEFAULT_SLOT_TIME_ZONE,
  );
  return {
    startDate: `${year}-${month}-${day}`,
    startTime: `${hour}:${minute}`,
  };
}

// Resolves a wall-clock date + "HH:MM" in a named zone to its true UTC instant.
// Intl only maps instant -> zoned wall-clock, so invert it: guess that the
// wall-clock IS UTC, measure how far that guess lands from the target zone, and
// correct. The second pass settles DST boundaries, where the offset that applies
// to the corrected instant differs from the one the guess landed in.
function zonedWallClockToInstant(dateKey, timeOfDay, timeZone) {
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hour, minute] = timeOfDay.split(":").map(Number);
  const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute, 0);

  const offsetAt = (instantMs) => {
    const parts = getWallClockParts(new Date(instantMs), timeZone);
    const zoned = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      0,
    );
    return zoned - instantMs;
  };

  const firstPass = wallClockAsUtc - offsetAt(wallClockAsUtc);
  return new Date(wallClockAsUtc - offsetAt(firstPass));
}

// The unambiguous instant for `start_date_time_iso_8601`. A converted slot is
// already one; a bare "HH:MM" only becomes one once paired with its date key and
// read in the slot list's stated zone.
export function formatSlotAsIso8601(slot, dateKey, timeZone) {
  if (isUtcIsoDateTime(slot)) {
    return new Date(slot).toISOString();
  }
  if (!dateKey || typeof slot !== "string" || !/^\d{2}:\d{2}$/.test(slot)) {
    return null;
  }
  const instant = zonedWallClockToInstant(
    dateKey,
    slot,
    timeZone || DEFAULT_SLOT_TIME_ZONE,
  );
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

// Slots come back keyed by their BE-side date, but a UTC instant's calendar day
// can differ from the one the member is shown — a late-evening slot moves to the
// previous/next date. Re-groups by the display zone's date so the date strip and
// its timeslots agree with the times printed on them. `timeZone` is the member's
// preference; without one this falls back to the browser's zone. A no-op when
// every slot is still the legacy bare "HH:MM" (flag off).
export function rebucketSlotsByLocalDate(dates, timeZone) {
  if (!dates || typeof dates !== "object") return dates;

  const hasConvertedSlots = Object.values(dates).some((clinicians) =>
    (clinicians || []).some(
      (clinician) => (clinician.timeslots || []).some(isUtcIsoDateTime),
    ),
  );
  if (!hasConvertedSlots) return dates;

  const rebucketed = {};
  Object.values(dates).forEach((clinicians) => {
    (clinicians || []).forEach((clinician) => {
      (clinician.timeslots || []).forEach((slot) => {
        if (!isUtcIsoDateTime(slot)) return;
        const dateKey = localDateKey(slot, timeZone);
        if (!rebucketed[dateKey]) rebucketed[dateKey] = [];
        let bucketClinician = rebucketed[dateKey].find(
          (existing) => existing.clinicianId === clinician.clinicianId,
        );
        if (!bucketClinician) {
          bucketClinician = { ...clinician, timeslots: [] };
          rebucketed[dateKey].push(bucketClinician);
        }
        bucketClinician.timeslots.push(slot);
      });
    });
  });

  // ISO-8601 instants still sort correctly as plain strings.
  Object.values(rebucketed).forEach((clinicians) =>
    clinicians.forEach((clinician) => clinician.timeslots.sort()),
  );

  return rebucketed;
}

// AUS-113: AU deployments use different API origins that don't support postcode lookup.
const AU_API_PREFIXES = [
  "https://api.spectrum-life.com.au",
  "https://api.spectrum-australia.com",
];

// Returns true when the host SPA is pointed at an Australian API origin.
// Used to gate features unavailable in AU (e.g. GB postcode lookup).
export function isAustralianApiUrl(apiUrl) {
  if (typeof apiUrl !== "string") return false;
  return AU_API_PREFIXES.some(
    (prefix) => apiUrl === prefix || apiUrl.startsWith(prefix),
  );
}

export function getFormatedDate(date, format, locale) {
  const m = moment(date);
  if (locale) m.locale(locale);
  return m.format(format);
}

export function isPostalCode(value) {
  if (typeof value !== "string") return false;
  return UK_POSTCODE_REGEX.test(value.trim());
}

export function validateEmailAddress(email) {
  if (typeof email !== "string") return false;

  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function getAgeFromDob(dob) {
  if (!dob) return 0;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return 0;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

export function validatePhoneNumber(phoneNo) {
  if (typeof phoneNo !== "string") return false;
  return /^\+?[0-9]{7,}$/.test(phoneNo.trim());
}

export function validateDob(dob, validation) {
  if (!dob) return false;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return false;
  const validationMaxDefault = 130;
  const maxYears = (validation && validation.max) || validationMaxDefault;
  const minYears = (validation && validation.min) || 0;

  const now = new Date();
  const age = getAgeFromDob(dob);

  if (birth > now) return false;
  if (age > maxYears) return false;
  if (age < minYears) return false;

  return true;
}

export function isValidField(value) {
  if (typeof value === "number") return true;
  if (typeof value === "string") return value.trim().length > 0;
  return false;
}

export function debounceInput(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export function getFormattedTimestamp() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

export function validateDmhAnswers(answers, question, apiUrl) {
  if (answers === null || typeof answers !== "object") return null;

  const result = {};
  const isAu = isAustralianApiUrl(apiUrl);

  const nonMandatoryFields = [
    "gp_email",
    ...question.options
      .map((q) => q.field_key)
      .filter((key) => key.includes("post_code")),
  ];

  if (question) {
    const fieldsWithoutValidation = question.options.filter((field) => {
      return (
        Object.prototype.hasOwnProperty.call(field, "validation") &&
        field.validation === false
      );
    });
    const fieldNamesWithoutValidation = fieldsWithoutValidation.map(
      (field) => field.field_key,
    );
    nonMandatoryFields.push(...fieldNamesWithoutValidation);
  }

  for (const [key, value] of Object.entries(answers)) {
    if (nonMandatoryFields.includes(key) && key.includes("email")) {
      result[key] = value.length === 0 ? true : validateEmailAddress(value);
    } else if (nonMandatoryFields.includes(key) && key.includes("dob")) {
      result[key] = value.length > 0 && getAgeFromDob(value) <= 120;
    } else if (key.includes("post_code")) {
      // AU deployments don't have a postcode lookup endpoint and AU postcodes
      // don't match the UK regex — accept any non-empty value instead.
      if (isAu) {
        result[key] = nonMandatoryFields.includes(key)
          ? true
          : isValidField(value);
      } else if (nonMandatoryFields.includes(key)) {
        result[key] = value.length > 0 ? isPostalCode(value) : true;
      } else {
        result[key] = isPostalCode(value);
      }
    } else if (key.includes("phone_number")) {
      result[key] = nonMandatoryFields.includes(key)
        ? true
        : validatePhoneNumber(value);
    } else if (nonMandatoryFields.includes(key) && value.length === 0) {
      result[key] = true;
    } else if (key === "address") {
      result[key] = value.length > 9;
    } else if (key.includes("email")) {
      result[key] = validateEmailAddress(value);
    } else if (key.includes("dob")) {
      const validation = question.options.filter((option) => {
        return (
          option.field_key.includes("dob") &&
          Object.prototype.hasOwnProperty.call(option, "validation")
        );
      })[0]?.validation;
      result[key] = validateDob(value, validation);
    } else {
      result[key] = isValidField(value);
    }
  }
  return result;
}

// Send the member back to the very top after the journey advances.
//
// scrollIntoView is not enough here: it aligns the element's own top, so anything
// rendered above it (progress bar, headings) stays scrolled out of view. The host
// also decides what actually scrolls — the window, an inner container, or an iframe
// document — so reset the window AND every scrollable ancestor of the element.
export function scrollToTop(element) {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  try {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (e) {
    window.scrollTo(0, 0);
  }
  if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;

  let node = element && element.parentElement;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    const scrolls =
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight;
    if (scrolls) node.scrollTop = 0;
    node = node.parentElement;
  }
}

export function isEmbedded() {
  return typeof window !== "undefined" && window.parent !== window.self;
}

// Contact values from the EAP config: a bare number, or "Text 'Hi' to +44 ..." for WhatsApp/SMS.
const CONTACT_INSTRUCTION_REGEX = /^(?:Text\s+'([^']*)'\s+to\s+)?(\+?[\d\s()-]+)$/i;

function parseContactInstruction(contactValue) {
  const match = CONTACT_INSTRUCTION_REGEX.exec(contactValue.trim());
  if (!match) return null;
  return { message: match[1] ?? "", number: match[2].replace(/[\s()-]/g, "") };
}

export function buildWhatsAppHref(contactValue) {
  const contact = parseContactInstruction(contactValue);
  if (!contact) return null;
  return `https://wa.me/${contact.number.replace(/\D/g, "")}`;
}

export function buildSmsHref(contactValue) {
  const contact = parseContactInstruction(contactValue);
  if (!contact) return null;
  const messageQuery = contact.message ? `?body=${encodeURIComponent(contact.message)}` : "";
  return `sms:${contact.number}${messageQuery}`;
}

// Takes the first phone-like run, so "0800 316 9337 (Freephone)" still dials.
const DIALABLE_NUMBER_REGEX = /\+?\d[\d\s()-]{5,}\d/;

export function buildTelHref(contactValue) {
  const match = DIALABLE_NUMBER_REGEX.exec(contactValue);
  if (!match) return null;
  return `tel:${match[0].replace(/[\s()-]/g, "")}`;
}

export function buildEapContactHref(contactLabel, contactValue) {
  const labelLower = contactLabel.toLowerCase();
  if (labelLower.includes("whatsapp")) return buildWhatsAppHref(contactValue);
  if (labelLower.includes("sms")) return buildSmsHref(contactValue);
  return buildTelHref(contactValue);
}
